import { useCallback, useEffect, useMemo, useState } from "react";
import { tr } from "../i18n/tr";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ArrowLeft, Loader2, BookOpen, Users, MessageCircle, Calendar,
  CheckCircle2, Lock, ChevronRight, Unlock, Clock, MapPin, Trophy,
  Eye, EyeOff, CircleDot, ClipboardList, PenLine, Maximize2,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import SessionChat from "../components/SessionChat";
import { useSecretMode, Hidden } from "../contexts/SecretModeContext";
import { BlockRenderer, THEMES } from "./formation/BlockEditor";
import { ScormPlayer } from "../components/lms/ScormPlayer";
import { useAppConfig, filterScormOnly } from "../hooks/useAppConfig";
import { useOfflineApi } from "../lib/offline/offlineApi";
import { OfflineBar } from "../components/offline/OfflineBar";
import { AttendanceKiosk } from "../components/offline/AttendanceKiosk";
import { ProjectionMode } from "../components/offline/ProjectionMode";
import { TrainerEvaluations } from "../components/offline/TrainerEvaluations";

const TABS = [
  { k: "cours", l: tr("lms.modules_bf17", "Modules"), icon: BookOpen },
  { k: "stagiaires", l: tr("lms.stagiaires_11bb", "Stagiaires"), icon: Users },
  { k: "emargement", l: tr("lms.emargement_7818", "Émargement"), icon: PenLine, feature: "emargement" },
  { k: "evaluations", l: tr("lms.evaluations_c758", "Évaluations"), icon: ClipboardList, feature: "evaluations" },
  { k: "chat", l: tr("lms.messagerie_d978", "Messagerie"), icon: MessageCircle, feature: "messagerie" },
];

export default function FormateurSessionDetailPage() {
  const { session_id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  // Iter 321 — API hors ligne : lectures en cache, actions en file d'attente
  const api = useOfflineApi("trainer:" + session_id);
  const [projecting, setProjecting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [myProgress, setMyProgress] = useState({ completed_sequence_ids: [], progress: 0 });
  const [trainerCap, setTrainerCap] = useState({ unlocked_sequence_ids: [], cap_percent: 0 });
  const [tab, setTab] = useState("cours");
  const [selectedSeqId, setSelectedSeqId] = useState(null);
  const [moduleData, setModuleData] = useState(null);
  const [loadingMod, setLoadingMod] = useState(false);
  const { enabled: secret, toggle: toggleSecret } = useSecretMode();

  const load = useCallback(async () => {
    try {
      const [sRes, eRes, tpRes] = await Promise.all([
        api.get("/lms/sessions/" + session_id),
        api.get("/lms/sessions/" + session_id + "/enrollments"),
        api.get("/lms/sessions/" + session_id + "/trainer-progress"),
      ]);
      setSession(sRes.data);
      setEnrollments(eRes.data || []);
      setTrainerCap(tpRes.data || { unlocked_sequence_ids: [], cap_percent: 0 });
      const tr = (tpRes.data?.trainers || []).find((t) => t.user_id === user?.user_id);
      setMyProgress({
        completed_sequence_ids: tpRes.data?.unlocked_sequence_ids || [],
        progress: tr?.progress || tpRes.data?.cap_percent || 0,
      });
    } catch (e) { toast.error(e.offline ? "Session non disponible hors ligne — préparez-la lorsque vous êtes connecté." : (e.response?.data?.detail || e.message)); }
    setLoading(false);
  // eslint-disable-next-line
  }, [session_id]);

  useEffect(() => { load(); }, [load]);

  // Iter 320 — Builder par société : fonctions actives + identité visuelle de la société de la session
  const { features, isOn } = useAppConfig({ sessionId: session_id });
  const sequences = filterScormOnly(session?.sequences || [], features);
  useEffect(() => {
    if (!sequences.length || selectedSeqId) return;
    setSelectedSeqId(sequences[0]?.sequence_id);
  }, [sequences, selectedSeqId]);

  useEffect(() => {
    if (!selectedSeqId) return;
    setLoadingMod(true);
    api.get(`/student/sessions/${session_id}/sequences/${selectedSeqId}/content`)
      .then((r) => setModuleData(r.data))
      .catch(() => setModuleData(null))
      .finally(() => setLoadingMod(false));
  // eslint-disable-next-line
  }, [selectedSeqId, session_id]);

  const unlockStep = async (seq_id) => {
    try {
      const r = await api.post(`/formateur/sessions/${session_id}/complete-sequence`, { sequence_id: seq_id });
      if (r.queued) {
        // Hors ligne : mise à jour optimiste locale + du cache
        const add = (ids) => Array.from(new Set([...(ids || []), seq_id]));
        setMyProgress((p) => ({ ...p, completed_sequence_ids: add(p.completed_sequence_ids) }));
        setTrainerCap((c) => ({ ...c, unlocked_sequence_ids: add(c.unlocked_sequence_ids) }));
        api.patchCache(`/lms/sessions/${session_id}/trainer-progress`, (d) => ({ ...(d || {}), unlocked_sequence_ids: add(d?.unlocked_sequence_ids) }));
        toast.success(tr("lms.etape_deverrouillee_hors_ligne_4363", "Etape deverrouillee (hors ligne) — synchronisation au retour du reseau."));
        return;
      }
      toast.success(tr("lms.etape_deverrouillee_pour_les_c3aa", "Etape deverrouillee pour les stagiaires."));
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const completedSet = useMemo(() => new Set(myProgress.completed_sequence_ids || []), [myProgress]);
  const selectedSeq = sequences.find((s) => s.sequence_id === selectedSeqId);
  const seqIndex = sequences.findIndex((s) => s.sequence_id === selectedSeqId);
  const theme = THEMES[moduleData?.module?.theme || "blitzz"] || THEMES.blitzz;

  if (loading || !session) {
    return <div className="py-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: "var(--brand-primary, #f7c935)" }} /></div>;
  }

  return (
    <div className="min-h-screen" style={{ background: "radial-gradient(circle at 100% 0%, #1a1130 0%, #0b0811 60%)" }} data-testid="formateur-detail-page">
      {/* HERO */}
      <div className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 80% 50%, var(--brand-primary, #f7c935) 0%, transparent 50%)" }} />
        <div className="relative max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => navigate("/formateur/sessions")} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white" data-testid="back-to-trainer-list">
              <ArrowLeft className="w-3.5 h-3.5" /> {tr("lms.mes_sessions_46a2", "Mes sessions")}
            </button>
            <button
              onClick={toggleSecret}
              className={"flex items-center gap-2 text-xs font-bold px-3 py-2 rounded-lg border transition " + (secret ? "bg-[var(--brand-primary,#f7c935)]/15 border-[var(--brand-primary,#f7c935)]/40 text-[var(--brand-primary,#f7c935)]" : "bg-white/5 border-white/10 text-slate-300 hover:text-white")}
              title={tr("lms.flou_toutes_les_infos_27d7", "Flou toutes les infos sensibles (noms, emails, tel)")}
              data-testid="secret-mode-toggle"
            >
              {secret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              Mode secret {secret ? "actif" : "off"}
            </button>
          </div>

          <div className="grid lg:grid-cols-3 gap-6 items-end">
            <div className="lg:col-span-2">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-1" style={{ color: "var(--brand-primary, #f7c935)" }}>{session.program_title}</div>
              <h1 className="text-3xl lg:text-5xl font-black tracking-tight text-white leading-tight">{session.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-300">
                <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {session.start_date} → {session.end_date}</span>
                {session.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {session.location}</span>}
                {session.modality && <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {session.modality}</span>}
                <Hidden as="span" className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {enrollments.length} stagiaire{enrollments.length > 1 ? "s" : ""}</Hidden>
              </div>
            </div>
            <div className="bg-[#14101c]/70 backdrop-blur border border-white/10 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Ma progression (cap)</span>
                <Trophy className="w-4 h-4" style={{ color: "var(--brand-primary, #f7c935)" }} />
              </div>
              <div className="text-4xl font-black text-white leading-none">{trainerCap.cap_percent || 0}%</div>
              <div className="bg-white/5 rounded-full h-1.5 overflow-hidden mt-3">
                <div className="h-full rounded-full" style={{ width: (trainerCap.cap_percent || 0) + "%", background: "var(--brand-primary, #f7c935)" }} />
              </div>
              <div className="mt-3 text-[10px] text-slate-500">{completedSet.size}/{sequences.length} etapes deverrouillees</div>
            </div>
          </div>

          {/* TABS */}
          <div className="flex gap-1 border-b border-white/10 mt-6 -mb-px overflow-x-auto">
            {TABS.filter((t) => !t.feature || isOn(t.feature)).map((t) => {
              const Icon = t.icon;
              return (
                <button key={t.k} onClick={() => setTab(t.k)} data-testid={"trainer-tab-" + t.k}
                  className={"flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold border-b-2 whitespace-nowrap " + (tab === t.k ? "border-[var(--brand-primary,#f7c935)] text-[var(--brand-primary,#f7c935)]" : "border-transparent text-slate-400 hover:text-white")}>
                  <Icon className="w-3.5 h-3.5" />{t.l}
                  {t.k === "stagiaires" && <Hidden as="span" className="text-[9px] px-1.5 py-0.5 rounded bg-white/10 text-white">{enrollments.length}</Hidden>}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-5"><OfflineBar api={api} sessionId={session_id} role="trainer" enabled={isOn("hors_ligne")} /></div>
        {projecting && selectedSeq && (
          <ProjectionMode api={api} sequences={sequences} index={seqIndex} moduleData={moduleData} onIndex={(i) => setSelectedSeqId(sequences[i]?.sequence_id)} onExit={() => setProjecting(false)} />
        )}
        {tab === "cours" && (
          <div className="grid lg:grid-cols-12 gap-6">
            <aside className="lg:col-span-4 xl:col-span-3">
              <div className="bg-[#14101c] border border-white/10 rounded-2xl p-3 sticky top-4">
                <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2 px-2">{tr("lms.modules_bf17", "Modules")}</div>
                {sequences.length === 0 && <div className="text-center py-8 text-slate-500 text-xs">{tr("lms.aucun_module_publie_501d", "Aucun module publie.")}</div>}
                <div className="space-y-1">
                  {sequences.map((seq, i) => {
                    const unlocked = completedSet.has(seq.sequence_id);
                    const isActive = selectedSeqId === seq.sequence_id;
                    const newLesson = seq.lesson_id && (i === 0 || sequences[i - 1].lesson_id !== seq.lesson_id);
                    return (
                      <div key={seq.sequence_id}>
                      {newLesson && <div className="text-[10px] uppercase tracking-wider text-sky-400 font-bold px-2.5 pt-3 pb-1 truncate" data-testid={"trainer-lesson-" + seq.lesson_id}>{seq.lesson_title}</div>}
                      <div className={"rounded-lg " + (isActive ? "bg-[var(--brand-primary,#f7c935)]/10 border border-[var(--brand-primary,#f7c935)]/30" : "hover:bg-white/5 border border-transparent")}>
                        <button onClick={() => setSelectedSeqId(seq.sequence_id)}
                          className="w-full text-left flex items-start gap-2 px-2.5 py-2"
                          data-testid={"trainer-seq-" + seq.sequence_id}
                        >
                          <div className="flex-shrink-0 mt-0.5">
                            {unlocked ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <CircleDot className="w-4 h-4 text-slate-500" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-[10px] text-slate-500 font-semibold">Séquence {i + 1}</div>
                            <div className={"text-xs font-semibold truncate " + (isActive ? "text-white" : "text-slate-300")}>{seq.title}</div>
                          </div>
                          {isActive && <ChevronRight className="w-3 h-3 text-slate-400 flex-shrink-0 mt-1" />}
                        </button>
                        {isActive && !unlocked && isOn("deverrouillage") && (
                          <div className="px-2.5 pb-2">
                            <Button size="sm" onClick={() => unlockStep(seq.sequence_id)} className="w-full bg-[var(--brand-primary,#f7c935)] text-[#14101c] font-bold text-[10px]" data-testid={"unlock-seq-" + seq.sequence_id}>
                              <Unlock className="w-3 h-3 mr-1" /> {tr("lms.deverrouiller_pour_les_stagiaires_4667", "Deverrouiller pour les stagiaires")}
                            </Button>
                          </div>
                        )}
                        {isActive && unlocked && (
                          <div className="px-2.5 pb-2 text-[10px] text-emerald-400/80 font-semibold flex items-center gap-1">
                            <Unlock className="w-2.5 h-2.5" /> {tr("lms.disponible_pour_les_stagiaires_94bc", "Disponible pour les stagiaires")}
                          </div>
                        )}
                      </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </aside>

            <main className="lg:col-span-8 xl:col-span-9">
              <div className="bg-[#14101c] border border-white/10 rounded-2xl p-6 lg:p-8 min-h-[500px]">
                {!selectedSeq ? (
                  <div className="py-16 text-center text-slate-500">
                    <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    <div className="text-sm">{tr("lms.selectionnez_un_module_cc9f", "Selectionnez un module.")}</div>
                  </div>
                ) : loadingMod ? (
                  <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: "var(--brand-primary, #f7c935)" }} /></div>
                ) : (
                  <>
                    <div className="mb-5 pb-5 border-b border-white/10 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-[var(--brand-primary,#f7c935)] font-bold mb-1">Module {seqIndex + 1}</div>
                      <h2 className="text-2xl font-black text-white">{moduleData?.module?.title || selectedSeq.title}</h2>
                      {moduleData?.module?.description && <p className="text-sm text-slate-400 mt-1">{moduleData.module.description}</p>}
                      </div>
                      {isOn("projection") && (
                        <Button onClick={() => setProjecting(true)} variant="outline" className="border-white/15 text-white shrink-0" data-testid="projection-btn">
                          <Maximize2 className="w-4 h-4 mr-1" /> {tr("lms.projeter_cd90", "Projeter")}
                        </Button>
                      )}
                    </div>
                    {moduleData?.module?.kind === "scorm" ? (
                      <ScormPlayer sequence={{ sequence_id: moduleData.module.module_id, scorm_entry: moduleData.module.scorm_entry, scorm_version: moduleData.module.scorm_version, title: moduleData.module.title }} enrollmentId={null} api={api} />
                    ) : (moduleData?.blocks || []).length === 0 ? (
                      <div className="py-12 text-center text-slate-500 text-sm">{tr("lms.ce_module_n_a_c468", "Ce module n'a pas encore de contenu. Editez-le dans le Formation Builder.")}</div>
                    ) : (
                      <div className="space-y-5" style={{ color: theme.text }}>
                        {(moduleData?.blocks || []).map((b, i) => <div key={i}><BlockRenderer block={b} theme={theme} /></div>)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </main>
          </div>
        )}

        {tab === "stagiaires" && (
          <div className="bg-[#14101c] border border-white/10 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5 text-[10px] uppercase tracking-wider text-slate-400">
                  <th className="text-left px-4 py-3 font-bold">{tr("lms.stagiaire_7e2d", "Stagiaire")}</th>
                  <th className="text-left px-4 py-3 font-bold">{tr("lms.email_ce8a", "Email")}</th>
                  <th className="text-left px-4 py-3 font-bold">{tr("lms.tel_2e00", "Tel")}</th>
                  <th className="text-left px-4 py-3 font-bold">{tr("lms.societe_7ad1", "Societe")}</th>
                  <th className="text-left px-4 py-3 font-bold">{tr("lms.statut_87a8", "Statut")}</th>
                  <th className="text-left px-4 py-3 font-bold">{tr("lms.progression_b97d", "Progression")}</th>
                </tr>
              </thead>
              <tbody>
                {enrollments.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-500 text-sm">{tr("lms.aucun_stagiaire_inscrit_c174", "Aucun stagiaire inscrit.")}</td></tr>}
                {enrollments.map((e) => (
                  <tr key={e.enrollment_id} className="border-t border-white/5 hover:bg-white/5">
                    <td className="px-4 py-3"><Hidden as="span" className="text-sm font-semibold text-white">{e.student_name}</Hidden></td>
                    <td className="px-4 py-3"><Hidden as="span" className="text-xs text-slate-400">{e.student_email}</Hidden></td>
                    <td className="px-4 py-3"><Hidden as="span" className="text-xs text-slate-400">{e.student_phone || "-"}</Hidden></td>
                    <td className="px-4 py-3"><Hidden as="span" className="text-xs text-slate-400">{e.student_company || "-"}</Hidden></td>
                    <td className="px-4 py-3"><span className="text-[9px] px-2 py-1 rounded bg-white/10 text-white font-bold uppercase">{e.status}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-white/5 rounded-full h-1 overflow-hidden max-w-[80px]">
                          <div className="h-full rounded-full" style={{ width: (e.progress || 0) + "%", background: "#10b981" }} />
                        </div>
                        <span className="text-xs text-white font-bold w-8 text-right">{e.progress || 0}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "emargement" && (
          <div className="max-w-4xl mx-auto">
            <AttendanceKiosk api={api} sessionId={session_id} enrollments={enrollments} mode="kiosk" trainerName={user?.name || ""} />
          </div>
        )}

        {tab === "evaluations" && (
          <div className="max-w-4xl mx-auto">
            <TrainerEvaluations api={api} sessionId={session_id} />
          </div>
        )}

        {tab === "chat" && (
          <div className="bg-[#14101c] border border-white/10 rounded-2xl p-5 max-w-4xl mx-auto">
            <SessionChat sessionId={session_id} api={api} currentUser={user} />
          </div>
        )}
      </div>
    </div>
  );
}
